import React, { useEffect, useRef, useState } from 'react';
import { getMcpPurchasedWallets, purchaseMcpCredits, type McpPurchasedWallet } from '../api.js';
import { useAuth } from '../context/AuthContext.js';

export default function McpCreditWallet(): React.ReactElement | null {
  const { user } = useAuth();
  return user ? <WalletState key={user.id} /> : null;
}
function WalletState(): React.ReactElement {
  const [wallets, setWallets] = useState<McpPurchasedWallet[] | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // Retain the same request on a lost response. A retry cannot create a second checkout.
  const request = useRef<{ id: string; membership: string; block: '25' | '50' | '100' } | null>(null);
  useEffect(() => {
    let active = true;
    setWallets(null);
    void getMcpPurchasedWallets().then((result: { wallets: McpPurchasedWallet[] }): void => {
      if (active) setWallets(result.wallets);
    }).catch((): void => { if (active) setMessage('Credit balance unavailable. Refresh to retry.'); });
    return (): void => { active = false; };
  }, [refresh]);
  async function purchase(membership: string, block: '25' | '50' | '100'): Promise<void> {
    if (busy) return;
    if (request.current && (request.current.membership !== membership || request.current.block !== block)) {
      setMessage('Retry the original purchase before selecting another block.'); return;
    }
    request.current ??= { id: crypto.randomUUID(), membership, block };
    setBusy(true); setMessage('Opening secure checkout...');
    try {
      const result = await purchaseMcpCredits(request.current.id, membership, block);
      if (result.status === 'paid') {
        request.current = null; setMessage('Payment received.'); setRefresh((value: number): number => value + 1);
      } else if (result.url && new URL(result.url).hostname === 'checkout.stripe.com') {
        window.location.assign(result.url);
      } else { setMessage('Checkout unavailable. Retry the same purchase.'); }
    } catch { setMessage('Checkout unavailable. Retry the same purchase.'); }
    finally { setBusy(false); }
  }
  return <section className="card" aria-label="Purchased MCP credits" style={{ padding: 18 }}>
    <h3>Purchased MCP credits</h3>
    <p>Usage draws only from purchased credits. Each run reserves 1,000 credits. Unused credits return to your spendable balance; failed and capped runs consume credits for incurred spend.</p>
    {wallets === null && !message && <p role="status">Loading credit balance...</p>}
    {wallets?.length === 0 && <p>Credit purchases become available when your paid workspace is ready.</p>}
    {wallets?.map((wallet: McpPurchasedWallet): React.ReactElement => <div key={wallet.membership_id}>
      <h4>{wallet.name}</h4>
      <p>{wallet.spendable_credits.toLocaleString()} spendable credits (${(wallet.spendable_credits / 100).toFixed(2)} purchased value) / {wallet.reserved_credits.toLocaleString()} reserved / {wallet.burned_credits.toLocaleString()} used</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {(['25', '50', '100'] as const).map((block: '25' | '50' | '100'): React.ReactElement =>
          <button type="button" className="btn" key={block} disabled={busy}
            onClick={(): void => { void purchase(wallet.membership_id,block); }}>
            ${block} / {(Number(block)*100).toLocaleString()} credits
          </button>)}
      </div>
    </div>)}
    {message && <p role="status">{message}</p>}
    <button type="button" className="btn" disabled={busy} onClick={(): void => { setMessage(''); setRefresh((value: number): number => value + 1); }}>Refresh balance</button>
  </section>;
}